import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useChildAttention } from '../../hooks/useChildAttention';
import { useSyncParentContext } from '../../hooks/useSyncParentContext';

const SESSION_KEY = 'sproutly_other_children_notice';
// A throwing sessionStorage (blocked site data) counts as seen: never nag when we can't remember the dismissal.
const seen = () => { try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return true; } };
const markSeen = () => { try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ } };
// It floats over the page (the tab bar on a phone, bottom-right controls on a
// desktop), so it gets out of the way on its own.
export const AUTO_HIDE_MS = 8000;

/** Pure: which other child to name, and how many more. Exported for the test. */
export function pickNotice(children: Array<{ id: string; firstName: string; displayName: string }>, byChild: Record<string, { total: number }>, activeId?: string) {
  const others = children.filter((c) => c.id !== activeId && (byChild[c.id]?.total ?? 0) > 0).sort((a, b) => byChild[b.id].total - byChild[a.id].total);
  if (others.length === 0) return null;
  const first = others[0];
  const count = byChild[first.id].total;
  return { childId: first.id, text: `${first.firstName || first.displayName} has ${count} thing${count === 1 ? '' : 's'} waiting${others.length > 1 ? `, and ${others.length - 1} more child${others.length > 2 ? 'ren do' : ' does'}` : ''}` };
}

/**
 * Once per session, on launch: tells a parent on one child that another needs them.
 * Marked seen the moment it is shown (not on dismiss), so a reload never brings it
 * back; the chosen notice is frozen so switching child does not re-target it.
 * Hides itself after AUTO_HIDE_MS if the parent neither opens nor dismisses it.
 */
const OtherChildrenNotice: React.FC = () => {
  const { activeStudent, children, isLoading, goToStudent } = useActiveStudent();
  const { byChild, isLoading: attentionLoading } = useChildAttention();
  const sync = useSyncParentContext();
  const [dismissed, setDismissed] = useState(seen);
  const [shown, setShown] = useState<{ childId: string; text: string } | null>(null);
  const ready = !dismissed && !isLoading && !attentionLoading && !!activeStudent && children.length >= 2;
  const activeId = activeStudent?.id;
  const candidate = useMemo(
    () => (ready && !shown && activeId ? pickNotice(children, byChild, activeId) : null),
    [ready, shown, children, byChild, activeId]
  );
  useEffect(() => {
    if (!candidate) return;
    markSeen();
    setShown(candidate);
  }, [candidate]);
  useEffect(() => {
    if (!shown || dismissed) return;
    const timer = setTimeout(() => setDismissed(true), AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [shown, dismissed]);
  const notice = shown ?? candidate;
  // Child pages only: Open calls goToStudent, which must never run from a family page.
  if (dismissed || !activeStudent || !notice) return null;
  const close = () => { markSeen(); setDismissed(true); };
  const go = async () => {
    close();
    const child = children.find((c) => c.id === notice.childId)!;
    await sync(child.locationId, child.organizationId);
    goToStudent(child.id, '');
  };
  return (
    <div data-testid="other-children-notice" role="status" className="fixed inset-x-3 bottom-20 lg:bottom-4 lg:left-auto lg:right-4 lg:w-96 z-30 rounded-lg bg-gray-900 text-white shadow-lg px-4 py-3 flex items-center gap-3">
      <button type="button" onClick={go} data-testid="other-children-notice-switch" className="flex-1 text-left text-sm">{notice.text}. <span className="underline">Open</span></button>
      <button type="button" onClick={close} data-testid="other-children-notice-dismiss" aria-label="Dismiss" className="text-gray-300 hover:text-white"><XMarkIcon className="h-5 w-5" /></button>
    </div>
  );
};
export default OtherChildrenNotice;
